import { Alert, Button, Card, Title } from '@mantine/core';
import { BaseLayout, LoadingAnimation, Select, variants } from '@uag/react-core';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { NumberInput, ScenarioTable } from 'components';
import { useConcreteMixtures, useForecast, useTargetSettings } from 'hooks';
import { Co2Footprint, Scenario, Structure, TargetSettings } from 'models';
import { Comparison } from 'pages';
import { ScenarioProvider } from 'providers';

export const Home = () => {
    const { t } = useTranslation();
    const forecast = useForecast();

    const targetSettingsRequest = useTargetSettings();
    const concreteMixturesRequest = useConcreteMixtures();

    const [comparison, setComparison] = useState<boolean>(false);

    const [targetSettings, setTargetSettings] = useState<TargetSettings>({});
    const [co2Footprint, setCo2Footprint] = useState<Co2Footprint>({});
    const [scenarios, setScenarios] = useState<Scenario[]>([]);
    const [validationError, setValidationError] = useState<string | null>(null);

    // Loading
    const isLoading = targetSettingsRequest.isLoading || concreteMixturesRequest.isLoading;
    if (isLoading) {
        return (
            <LoadingAnimation text={t('loadingConcreteData')} />
        );
    }

    // Error
    const errorMessage = targetSettingsRequest.errorMessage || concreteMixturesRequest.errorMessage;
    if (errorMessage !== null) {
        return (
            <Alert title={t('serverError')} variant={variants.banner.alert}>
                {errorMessage}
            </Alert>);
    }

    // Insufficient data
    const structures = targetSettingsRequest.structures;
    if (structures.length === 0) {
        return (
            <Alert title={t('insufficientData')} variant={variants.banner.alert}>
                {t('noStructures')}
            </Alert>
        );
    }

    const concreteMixtures = concreteMixturesRequest.concreteMixtures;
    if (concreteMixtures.length === 0) {
        return (
            <Alert title={t('insufficientData')} variant={variants.banner.alert}>
                {t('noConcreteMixtures')}
            </Alert>
        );
    }

    // Init default values
    const setDefaultValues = (structure?: Structure) => {
        const defaultValues = targetSettingsRequest.defaultValues.find(defaultValues => defaultValues.structureId === structure?.id);

        setTargetSettings({
            structure,
            targetTime: defaultValues?.targetTime,
            targetValue: defaultValues?.targetValue
        });

        setCo2Footprint({
            co2Electricity: defaultValues?.carbonFootprintElectricity,
            concreteVolume: defaultValues?.volumeOfConcrete,
            formworkSurface: defaultValues?.surfaceOfFormwork,
            electricityCosts: defaultValues?.costsOfElectricity
        });

        setScenarios([{
            id: Date.now() % 1000000,
            ambientTemperature: defaultValues?.ambientTemperature || 0,
            freshConcreteTemperature: defaultValues?.freshConcreteTemperature || 0,
            concreteMixture: undefined,
            calibrationCurve: undefined,
            concretePrice: defaultValues?.priceOfConcrete || 0
        }]);
    };

    if (targetSettings.structure === undefined) {
        const structure = structures[0];
        setDefaultValues(structure);
    }


    return (
        <ScenarioProvider value={scenarios}>
            {comparison ?
                <Comparison
                    co2Footprint={co2Footprint}
                    targetSettings={targetSettings}
                    onClose={() => setComparison(false)} /> :
                <BaseLayout
                    background="gray"
                    className="bg-background-gray-ultra-ultra-light"
                    innerClassName="h-full mx-auto min-w-[1280px] max-w-[1920px]"
                    versionNumber={APP_VERSION}>
                    <div className="flex flex-col gap-6">
                        <div className="flex gap-6">
                            <Card className="flex flex-col gap-4 p-6 min-h-44 w-full">
                                <Title order={4}>{t('targetSettings')}</Title>
                                <div className="flex flex-row gap-4 w-full">
                                    <NumberInput
                                        className="flex-1"
                                        label={t('targetTime')}
                                        max={168}
                                        min={2}
                                        required={true}
                                        unit="h"
                                        value={targetSettings.targetTime}
                                        onChange={(value) => setTargetSettings({
                                            ...targetSettings,
                                            targetTime: value === '' ? undefined : value as number
                                        })}
                                    />
                                    <Select
                                        className="flex-1"
                                        data={structures.map(structure => structure.name)}
                                        label={t('structure')}
                                        value={targetSettings.structure?.name}
                                        onChange={(value) => {
                                            const structure = structures.find(structure => structure.name === value as string);
                                            setDefaultValues(structure);
                                        }}
                                    />
                                    <NumberInput
                                        className="flex-1"
                                        label={t('targetValue')}
                                        min={0}
                                        required={true}
                                        unit="MPa"
                                        value={targetSettings.targetValue}
                                        onChange={(value) => setTargetSettings({
                                            ...targetSettings,
                                            targetValue: value === '' ? undefined : value as number
                                        })}
                                    />
                                </div>
                            </ Card>

                            <Card className="flex flex-col gap-4 p-6 min-h-44 w-full">
                                <Title order={4}>{t('co2Footprint')}</Title>
                                <div className="flex flex-row gap-4 w-full">
                                    <NumberInput
                                        className="flex-1"
                                        label={t('co2Electricity')}
                                        min={0}
                                        required={true}
                                        unit="g&nbsp;CO₂/kWh"
                                        value={co2Footprint.co2Electricity}
                                        onChange={(value) => setCo2Footprint({
                                            ...co2Footprint,
                                            co2Electricity: value === '' ? undefined : value as number
                                        })}
                                    />
                                    <NumberInput
                                        className="flex-1"
                                        label={t('concreteVolume')}
                                        min={0}
                                        required={true}
                                        unit="m³"
                                        value={co2Footprint.concreteVolume}
                                        onChange={(value) => setCo2Footprint({
                                            ...co2Footprint,
                                            concreteVolume: value === '' ? undefined : value as number
                                        })}
                                    />
                                    <NumberInput
                                        className="flex-1"
                                        label={t('formworkSurface')}
                                        min={0}
                                        required={true}
                                        unit="m²"
                                        value={co2Footprint.formworkSurface}
                                        onChange={(value) => setCo2Footprint({
                                            ...co2Footprint,
                                            formworkSurface: value === '' ? undefined : value as number
                                        })}
                                    />
                                    <NumberInput
                                        className="flex-1"
                                        label={t('electricityCosts')}
                                        min={0}
                                        required={true}
                                        unit="€/kWh"
                                        value={co2Footprint.electricityCosts}
                                        onChange={(value) => setCo2Footprint({
                                            ...co2Footprint,
                                            electricityCosts: value === '' ? undefined : value as number
                                        })}
                                    />
                                </div>
                            </Card>
                        </div>

                        <ScenarioTable
                            value={scenarios}
                            onChange={(value) => setScenarios(value)} />

                        <div className="flex gap-6 mb-6">
                            <Button
                                className="min-w-28"
                                loading={forecast.isLoading}
                                onClick={async () => {
                                    setValidationError(null);

                                    const { targetTime, structure, targetValue } = targetSettings;
                                    const { concreteVolume, formworkSurface, co2Electricity, electricityCosts } = co2Footprint;

                                    // Check for required input (handled by the NumberInput component)
                                    const missingNumberInput = (
                                        targetTime === undefined ||
                                        structure === undefined ||
                                        targetValue === undefined ||
                                        concreteVolume === undefined ||
                                        formworkSurface === undefined ||
                                        co2Electricity === undefined ||
                                        electricityCosts === undefined) ||
                                        scenarios.some(scenario =>
                                            scenario.ambientTemperature === undefined ||
                                            scenario.freshConcreteTemperature === undefined ||
                                            scenario.concretePrice === undefined
                                        );

                                    if (missingNumberInput) {
                                        return;
                                    }

                                    // Check if a concrete mixture is undefined
                                    const missingConcreteMixture = scenarios.some(scenario => scenario.concreteMixture === undefined);
                                    if (missingConcreteMixture) {
                                        setValidationError(t('missingConcreteMixture'));
                                        return;
                                    }

                                    // Check if a calibration curve is undefined
                                    const missingCalibrationCurve = scenarios.some(scenario => scenario.calibrationCurve === undefined);
                                    if (missingCalibrationCurve) {
                                        setValidationError(t('missingCalibrationCurve'));
                                        return;
                                    }

                                    // Check if the target value is in the range of the used calibration curves
                                    const scenarioOutOfRange = scenarios.find(
                                        scenario => (scenario.calibrationCurve!.minCalibrationCurveStrength > targetValue ||
                                            scenario.calibrationCurve!.maxCalibrationCurveStrength < targetValue));

                                    if (scenarioOutOfRange) {
                                        const { calibrationCurve } = scenarioOutOfRange;
                                        setValidationError(t('targetValueOutOfRange', {
                                            calibrationCurve: calibrationCurve!.name,
                                            targetValue,
                                            min: Math.ceil(calibrationCurve!.minCalibrationCurveStrength),
                                            max: Math.floor(calibrationCurve!.maxCalibrationCurveStrength)
                                        }));
                                        return;
                                    }

                                    const calculatedScenarios = await forecast.calculate({
                                        targetTime,
                                        structure,
                                        targetValue,
                                        surfaceOfFormwork: formworkSurface,
                                        volumeOfConcrete: concreteVolume,
                                        scenarios,
                                        carbonFootprintElectricity: co2Electricity / 1000, // kg
                                        costsOfElectricity: electricityCosts
                                    });

                                    if (calculatedScenarios.length > 0) {
                                        setScenarios(calculatedScenarios);
                                        setComparison(true);
                                    }
                                }} >
                                {t('calculate')}
                            </Button>
                            {validationError && (
                                <Alert title={t('validationError')} variant={variants.banner.alert}>
                                    {validationError}
                                </Alert>)}
                            {forecast.errorMessage && (
                                <Alert title={t('calculationError')} variant={variants.banner.alert}>
                                    {forecast.errorMessage}
                                </Alert>)}

                        </div>
                    </div>

                </BaseLayout >
            }
        </ScenarioProvider >
    );
};
